import { z } from "zod";
import { formatAfficheSchema, type SemaineAffiche } from "./affiche";

/**
 * Le contrat du planning de stream (lots T et P1, issue #393) : la semaine, ses creneaux, les
 * emissions de la bibliotheque, le kit « A poster », et les libelles que l'affiche et le texte du kit
 * partagent. Lu par le backend (les routes, la file de rendus) et, a la PR de la regie, par le frontal.
 *
 * Les dates se calculent en UTC a partir de la chaine ISO, jamais avec le fuseau de la machine : un
 * lundi est une date de calendrier, pas un instant.
 */

export const LIEN_CHAINE = "twitch.tv/kavalierogamedev";
export const DUREE_PAR_DEFAUT_MIN = 180;
/** Le nombre de creneaux d'une semaine : celui des avis que l'affiche sait composer. */
export const CRENEAUX_MAX = 7;

const DATE_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Le jour de la semaine d'une date ISO (1 = lundi, 7 = dimanche), ou `null` si ce n'est pas une date. */
function jourIso(date: string): number | null {
  const trouve = DATE_ISO.exec(date);
  if (trouve === null) return null;
  const [, annee, mois, jour] = trouve;
  const instant = new Date(Date.UTC(Number(annee), Number(mois) - 1, Number(jour)));
  // Un 31 fevrier deborde sur mars : la date relue differe de celle qu'on a donnee.
  if (instant.toISOString().slice(0, 10) !== date) return null;
  return instant.getUTCDay() === 0 ? 7 : instant.getUTCDay();
}

/** Une date ISO (AAAA-MM-JJ) qui existe et qui tombe un lundi. */
export const lundiSchema = z.string().refine((valeur) => jourIso(valeur) === 1, {
  message: "un lundi au format AAAA-MM-JJ est attendu",
});

/** Une heure de debut, « 21:30 ». */
export const heureSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, {
  message: "une heure au format HH:MM est attendue",
});

const contenuSchema = z.enum(["jeu", "emission", "evenement"]);
const etatCreneauSchema = z.enum(["prevu", "off"]);

export const creneauSchema = z.object({
  id: z.int(),
  jour: z.int().min(1).max(7), // 1 = lundi
  debut: heureSchema,
  dureeMin: z.int().min(30).max(1380),
  contenu: contenuSchema,
  etat: etatCreneauSchema,
  categorie: z.object({ twitchId: z.string(), nom: z.string() }).nullable(),
  emissionId: z.int().nullable(),
  titre: z.string().max(140).nullable(),
  invite: z.string().max(25).nullable(),
  visuelId: z.int().nullable(),
  /** Vrai quand le creneau differe de celui dont il a ete copie. */
  modifie: z.boolean(),
});
export type Creneau = z.infer<typeof creneauSchema>;

export const creneauCorpsSchema = z.strictObject({
  jour: z.int().min(1).max(7),
  debut: heureSchema,
  dureeMin: z.int().min(30).max(1380).default(DUREE_PAR_DEFAUT_MIN),
  contenu: contenuSchema,
  etat: etatCreneauSchema.default("prevu"),
  categorie: z
    .strictObject({ twitchId: z.string().min(1).max(20), nom: z.string().min(1).max(140) })
    .nullable(),
  emissionId: z.int().nullable(),
  titre: z.string().max(140).nullable(),
  invite: z
    .string()
    .regex(/^[A-Za-z0-9_]{1,25}$/)
    .nullable(),
  visuelId: z.int().nullable(),
});
export type CreneauCorps = z.infer<typeof creneauCorpsSchema>;

export const semaineSchema = z.object({
  lundi: lundiSchema,
  etat: z.enum(["brouillon", "publiee", "corrigee"]),
  version: z.int(),
  copieeDe: lundiSchema.nullable(),
  creneaux: z.array(creneauSchema),
});
export type Semaine = z.infer<typeof semaineSchema>;

export const creerSemaineCorpsSchema = z.strictObject({ lundi: lundiSchema });
export type CreerSemaineCorps = z.infer<typeof creerSemaineCorpsSchema>;

export const emissionSchema = z.object({
  id: z.int(),
  titre: z.string(),
  visuelId: z.int().nullable(),
});
export type Emission = z.infer<typeof emissionSchema>;
export const emissionCorpsSchema = z.strictObject({
  titre: z.string().min(1).max(140),
  visuelId: z.int().nullable(),
});
export type EmissionCorps = z.infer<typeof emissionCorpsSchema>;
export const emissionsReponseSchema = z.object({ emissions: z.array(emissionSchema) });

export const visuelCreeSchema = z.object({ id: z.int() });

/** Ce que la recherche d'un jeu rend : une categorie de Twitch, pour la liste de choix. */
export const categorieTrouveeSchema = z.object({
  twitchId: z.string(),
  nom: z.string(),
  /** L'adresse de la jaquette chez Twitch, pour la liste de choix seulement (jamais pour fabriquer un visuel). */
  vignette: z.string().nullable(),
  /**
   * L'adresse d'un visuel large a montrer avant le choix : le visuel deja en base (adresse locale) ou celui de Steam,
   * pour les cinq premiers resultats ; `null` sinon (le frontal retombe sur `vignette`). Jamais renvoyee au serveur :
   * le visuel se retrouve a l'enregistrement du creneau.
   */
  apercu: z.string().nullable(),
});
export type CategorieTrouvee = z.infer<typeof categorieTrouveeSchema>;
export const categoriesTrouveesSchema = z.object({
  categories: z.array(categorieTrouveeSchema).max(10),
});
export const rechercheJeuSchema = z.object({ q: z.string().max(400).optional() });

export const renduSchema = z.object({
  id: z.int(),
  format: formatAfficheSchema,
  sortie: z.enum(["png", "jpeg"]),
  etat: z.enum(["en_attente", "en_cours", "fait", "echoue"]),
  octets: z.int().nullable(),
});
export type Rendu = z.infer<typeof renduSchema>;

export const kitSchema = z.object({
  lundi: lundiSchema,
  version: z.int(),
  rendus: z.array(renduSchema),
  /** Une ligne par stream prevu, puis le lien de la chaine. */
  texte: z.string(),
  /** Faux quand le backend ne joint pas le service de rendu. */
  renduJoignable: z.boolean(),
});
export type Kit = z.infer<typeof kitSchema>;

export const NOMS_DES_JOURS: readonly string[] = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];

const NOMS_DES_MOIS: readonly string[] = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

function nomDuMois(indice: number): string {
  return NOMS_DES_MOIS[indice] ?? "";
}

/** Un quantieme : « 1er » pour le premier, le nombre sinon. */
function quantieme(jour: number): string {
  return jour === 1 ? "1er" : String(jour);
}

/**
 * « du 1er au 7 septembre », « du 29 septembre au 5 octobre », « du 29 decembre au 4 janvier ». Le lundi
 * doit etre une date ISO valide ; le dimanche est calcule en UTC.
 */
export function libelleSemaine(lundi: string): string {
  const trouve = DATE_ISO.exec(lundi);
  if (trouve === null) throw new Error("libelleSemaine : un lundi AAAA-MM-JJ est attendu");
  const debut = new Date(Date.UTC(Number(trouve[1]), Number(trouve[2]) - 1, Number(trouve[3])));
  const fin = new Date(debut.getTime() + 6 * 24 * 60 * 60 * 1000);
  const moisFin = nomDuMois(fin.getUTCMonth());
  if (debut.getUTCMonth() === fin.getUTCMonth()) {
    return `du ${quantieme(debut.getUTCDate())} au ${quantieme(fin.getUTCDate())} ${moisFin}`;
  }
  return `du ${quantieme(debut.getUTCDate())} ${nomDuMois(debut.getUTCMonth())} au ${quantieme(fin.getUTCDate())} ${moisFin}`;
}

/** « 21:00 » donne « 21h », « 21:30 » donne « 21h30 », « 09:05 » donne « 9h05 ». */
export function libelleHeure(debut: string): string {
  const [heures = "0", minutes = "00"] = debut.split(":");
  const h = String(Number(heures));
  return minutes === "00" ? `${h}h` : `${h}h${minutes}`;
}

type EmissionConnue = { readonly id: number; readonly titre: string };
type EmissionAvecVisuel = EmissionConnue & { readonly visuelId: number | null };

/** Les creneaux d'une semaine, tries par jour puis par heure (l'identifiant departage). */
function trier(creneaux: readonly Creneau[]): Creneau[] {
  return [...creneaux].sort(
    (a, b) => a.jour - b.jour || a.debut.localeCompare(b.debut) || a.id - b.id,
  );
}

/** Le titre d'un avis : le titre court d'abord, puis le nom du jeu ou de l'emission. */
function titreDe(creneau: Creneau, emission: EmissionConnue | undefined): string {
  if (creneau.titre !== null && creneau.titre !== "") return creneau.titre;
  const parContenu =
    creneau.contenu === "emission"
      ? (emission?.titre ?? creneau.categorie?.nom)
      : (creneau.categorie?.nom ?? emission?.titre);
  return parContenu ?? "";
}

/**
 * L'affiche d'une semaine. Les creneaux sont tries par jour puis par heure. Un creneau OFF devient un avis
 * sans heure ni titre. Le visuel d'un avis est celui du creneau, a defaut celui de son emission ; sa cle est
 * `v<identifiant>` et son adresse vient de `adresseDuVisuel` (une adresse du site pour l'apercu, une data
 * URL pour le rendu).
 */
export function versAffiche(
  semaine: Semaine,
  emissions: readonly EmissionAvecVisuel[],
  adresseDuVisuel: (visuelId: number) => string,
): SemaineAffiche {
  const visuels: Record<string, string> = {};
  const avis = trier(semaine.creneaux).map((creneau) => {
    const jour = NOMS_DES_JOURS[creneau.jour - 1] ?? "";
    if (creneau.etat === "off") {
      return { jour, heure: "", genre: "off" as const, titre: "", visuel: null, invite: null };
    }
    const emission =
      creneau.emissionId === null ? undefined : emissions.find((e) => e.id === creneau.emissionId);
    const visuelId = creneau.visuelId ?? emission?.visuelId ?? null;
    let visuel: string | null = null;
    if (visuelId !== null) {
      visuel = `v${visuelId}`;
      visuels[visuel] = adresseDuVisuel(visuelId);
    }
    return {
      jour,
      heure: libelleHeure(creneau.debut),
      genre: creneau.contenu,
      titre: titreDe(creneau, emission),
      visuel,
      invite: creneau.invite,
    };
  });
  return { libelle: libelleSemaine(semaine.lundi), avis, visuels };
}

/** Le texte du kit : « Lundi 21h : Cat Quest II », un creneau OFF n'y figure pas, le lien de la chaine finit. */
export function texteDuKit(semaine: Semaine, emissions: readonly EmissionConnue[]): string {
  const lignes = trier(semaine.creneaux)
    .filter((creneau) => creneau.etat === "prevu")
    .map((creneau) => {
      const emission =
        creneau.emissionId === null
          ? undefined
          : emissions.find((e) => e.id === creneau.emissionId);
      const jour = NOMS_DES_JOURS[creneau.jour - 1] ?? "";
      const titre = titreDe(creneau, emission);
      const invite = creneau.invite === null ? "" : ` avec ${creneau.invite}`;
      return `${jour} ${libelleHeure(creneau.debut)} : ${titre}${invite}`;
    });
  return [...lignes, "", LIEN_CHAINE].join("\n").replace(/^\n/, "");
}
